// src/components/MultiSelect.tsx
import React, { useState, useRef, useEffect } from 'react';
import './MultiSelect.css'; // Import the separate CSS file

interface MultiSelectProps {
  label: string;
  options: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  label,
  options,
  selected,
  onChange,
  placeholder = 'Select options',
  searchPlaceholder = 'Search...'
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Filter options based on search term
  const filteredOptions = options.filter(option => 
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Handle clicks outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Toggle option selection
  const toggleOption = (option: string) => {
    if (selected.includes(option)) {
      onChange(selected.filter(item => item !== option));
    } else {
      onChange([...selected, option]);
    }
  };
  
  // Clear all selections
  const clearSelections = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };
  
  // Remove a single selection
  const removeSelection = (option: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selected.filter(item => item !== option));
  };
  
  // Highlight matching text in search results
  const highlightMatch = (text: string, query: string) => {
    if (!query) return text;
    
    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);
    
    return (
      <>
        {parts.map((part, i) => 
          regex.test(part) ? 
            <span key={i} className="option-highlight">{part}</span> : 
            part
        )}
      </>
    );
  };
  
  return (
    <div className="filter-group">
      <label>{label}</label>
      <div className="multi-select-container" ref={dropdownRef}>
        {/* Header/trigger for the dropdown */}
        <div 
          className="multi-select-header"
          onClick={() => setIsOpen(!isOpen)}
        >
          {selected.length > 0 ? `${selected.length} selected` : placeholder}
          <span className={`dropdown-arrow ${isOpen ? 'open' : ''}`}>▼</span>
        </div>
        
        {/* Selected options as tags */}
        {selected.length > 0 && (
          <div className="selected-tags">
            {selected.map(option => (
              <div key={option} className="selected-tag">
                {option}
                <button 
                  className="tag-remove"
                  onClick={(e) => removeSelection(option, e)}
                >
                  ×
                </button>
              </div>
            ))}
            {selected.length > 0 && (
              <button 
                className="clear-button"
                onClick={clearSelections}
              >
                Clear
              </button>
            )}
          </div>
        )}
        
        {/* Dropdown content */}
        {isOpen && (
          <div className="multi-select-dropdown">
            <input
              type="text"
              className="dropdown-search"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
            
            <div className="options-list">
              {filteredOptions.map(option => (
                <div 
                  key={option}
                  className={`option-item ${selected.includes(option) ? 'selected' : ''}`}
                  onClick={() => toggleOption(option)}
                >
                  {searchTerm ? highlightMatch(option, searchTerm) : option}
                </div>
              ))}
              {filteredOptions.length === 0 && (
                <div className="no-options">No matching options found</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiSelect;